import React, { useState } from 'react';
import { X, Save } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';

const LiquidacionEditModal = ({ liquidacion, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    descuentos: liquidacion.descuentos || 0,
    anticipos: liquidacion.anticipos || 0,
    ajuste_monto: liquidacion.ajuste_monto || 0,
    numero_factura: liquidacion.numero_factura || '',
    numero_cheque: liquidacion.numero_cheque || '',
    observacion: liquidacion.observacion || ''
  });
  const [justification, setJustification] = useState('');
  const [saving, setSaving] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const numericFields = ['descuentos', 'anticipos', 'ajuste_monto'];
    setFormData(prev => ({
      ...prev,
      [name]: numericFields.includes(name) ? parseFloat(value) || 0 : value
    }));
  };

  const calculateAPagar = () => {
    const total = liquidacion.total_usd || 0;
    return total + (formData.ajuste_monto || 0) - (formData.descuentos || 0) - (formData.anticipos || 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!justification.trim()) {
      toast.error('La justificación es obligatoria');
      return;
    }

    setSaving(true);
    try {
      await onSave(formData, justification.toUpperCase());
    } catch (error) {
      console.error('Error saving:', error);
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '$0';
    return `$${Math.round(value).toLocaleString('es-CL')}`;
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
        {/* Header */}
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between bg-slate-50">
          <h2 className="text-sm font-bold text-slate-800">
            Editar Liquidación: {liquidacion.dispatch_number}
          </h2>
          <Button variant="ghost" size="sm" onClick={onClose} className="h-7 w-7 p-0">
            <X size={16} />
          </Button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Info readonly */}
          <div className="bg-slate-50 rounded-lg p-3 text-xs space-y-1">
            <p><span className="font-semibold">Transportista:</span> {liquidacion.company_name}</p>
            <p><span className="font-semibold">Total USD:</span> USD {formatCurrency(liquidacion.total_usd)}</p>
          </div>

          {/* Editable fields */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Descuentos (USD)</Label>
              <Input type="number" name="descuentos" value={formData.descuentos} onChange={handleChange} className="h-8 text-xs" min="0" step="1" />
            </div>
            <div>
              <Label className="text-xs">Anticipos (USD)</Label>
              <Input type="number" name="anticipos" value={formData.anticipos} onChange={handleChange} className="h-8 text-xs" min="0" step="1" />
            </div>
            <div>
              <Label className="text-xs text-purple-700">Ajuste (+/-) USD</Label>
              <Input type="number" name="ajuste_monto" value={formData.ajuste_monto} onChange={handleChange} className="h-8 text-xs border-purple-200" step="1" placeholder="ej: 500 o -200" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs">N° Factura</Label>
              <Input
                type="text"
                name="numero_factura"
                value={formData.numero_factura}
                onChange={handleChange}
                className="h-8 text-xs"
                placeholder="Ej: F-001234"
              />
            </div>
            <div>
              <Label className="text-xs">N° Cheque</Label>
              <Input
                type="text"
                name="numero_cheque"
                value={formData.numero_cheque}
                onChange={handleChange}
                className="h-8 text-xs"
                placeholder="Ej: CH-001234"
              />
            </div>
          </div>

          <div>
            <Label className="text-xs">Observaciones</Label>
            <Textarea
              name="observacion"
              value={formData.observacion}
              onChange={handleChange}
              className="text-xs min-h-[60px]"
              placeholder="Observaciones adicionales..."
            />
          </div>

          {/* Calculated A Pagar */}
          <div className="bg-blue-50 rounded-lg p-3 flex justify-between items-center">
            <span className="text-sm font-semibold text-slate-700">A PAGAR:</span>
            <span className="text-lg font-bold text-blue-600 font-mono">
              USD {formatCurrency(calculateAPagar())}
            </span>
          </div>

          {/* Justification */}
          <div>
            <Label className="text-xs text-red-600">Justificación (obligatoria) *</Label>
            <Textarea
              value={justification}
              onChange={(e) => setJustification(e.target.value)}
              className="text-xs min-h-[60px] border-red-200 focus:border-red-400"
              placeholder="Explique el motivo de la modificación..."
              required
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
            <Button type="button" variant="outline" size="sm" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" size="sm" disabled={saving}>
              <Save size={14} className="mr-1" />
              {saving ? 'Guardando...' : 'Guardar Cambios'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LiquidacionEditModal;
